import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { Bell, Check, Info, CheckCircle2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientNotifications = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/client/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/client/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle2 className="text-green-400" size={20} />;
      case 'warning': return <AlertCircle className="text-yellow-400" size={20} />;
      default: return <Info className="text-blue-400" size={20} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712]">
        <ClientNav />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="client-notifications-page">
      <ClientNav />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="notifications-title">Notifications</h1>
          <p className="text-slate-400">Stay updated on your projects and account activity</p>
        </div>

        {notifications.length === 0 ? (
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center" data-testid="no-notifications">
            <Bell className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No notifications yet</h3>
            <p className="text-slate-400">You'll see updates here when there's activity on your account</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="notifications-list">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-slate-950 border rounded-xl p-4 transition-all ${
                  notification.read
                    ? 'border-slate-800'
                    : 'border-indigo-500/50 bg-indigo-500/5'
                }`}
                data-testid={`notification-${notification.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="mt-1">{getIcon(notification.type)}</div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold mb-1">{notification.title}</h3>
                      <p className="text-slate-300 text-sm mb-2">{notification.message}</p>
                      <span className="text-xs text-slate-500">
                        {format(new Date(notification.created_at), 'MMM dd, yyyy h:mm a')}
                      </span>
                    </div>
                  </div>
                  {!notification.read && (
                    <Button
                      onClick={() => markAsRead(notification.id)}
                      variant="ghost"
                      size="sm"
                      className="text-indigo-400 hover:text-indigo-300"
                      data-testid={`mark-read-${notification.id}`}
                    >
                      <Check size={16} className="mr-1" />
                      Mark read
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientNotifications;
